import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { later, run, schedule, scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { tracked } from '@glimmer/tracking';

import type { PopupData, PopupListItem, RecordData, Service } from 'webapp-types';
import { ActivityLogEvent } from 'webapp/enums/activity-log';
import { BillingFeature, PopupEvent, TeamSectionItemType } from 'webapp/enums/enums';
import { TourId } from 'webapp/enums/tour';
import { TrackingEvent } from 'webapp/enums/tracking';
import { pick } from 'webapp/utils/common';
import { getMaxSizeForRatio } from 'webapp/utils/html';

import CommonCollapsiblePillOptionsComponent from 'webapp/components/common/collapsible-pill-options';
import CommonRenderingPreviewHeaderComponent from 'webapp/components/common/rendering/preview-header';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import NotificationsButtonComponent from 'webapp/components/notifications/button';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import ReportsViewerComponent from 'webapp/components/reports/viewer';
import VerificationBadgeComponent from 'webapp/components/verification/badge';

import type Report from 'webapp/models-custom/report';
import type ReportSlide from 'webapp/models-custom/report-slide';
import type Template from 'webapp/models-custom/template';

import type { TrackingOriginTypeForReportReminderPopupOpen } from 'webapp/services/model-manager/reminders';
import type {
	TrackingOriginForTemplateCreated,
	TrackingOriginTypeForPreview,
	TrackingOriginTypeForShare,
} from 'webapp/services/model-manager/reports';

type Action =
	| 'downloadSnapshot'
	| 'openSharePopup'
	| 'present'
	| 'presentWithNotes'
	| 'openAccountTemplateEditPopup'
	| 'toggleFavorite'
	| 'verify'
	| 'deleteReport'
	| 'duplicate'
	| 'addToTeam';

type Option = PopupListItem<Action>;

export default class ReportsPopupPreviewComponent extends PopupInnerModalComponent<PopupData.Report.Preview.Any> {
	@service('garbage-collector') declare garbageCollectorService: Service.GarbageCollector;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-previews')
	declare mmBoardPreviewsService: Service.ModelManager.BoardPreviews;
	@service('model-manager/reminders') declare mmRemindersService: Service.ModelManager.Reminders;
	@service('model-manager/activity-logs')
	declare mmActivityLogsService: Service.ModelManager.ActivityLogs;
	@service('model-manager/report-slides')
	declare mmReportSlidesService: Service.ModelManager.ReportSlides;
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;
	@service('store') declare storeService: Service.Store;
	@service('tour') declare tourService: Service.Tour;
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/team-sections')
	declare mmTeamSectionsService: Service.ModelManager.TeamSections;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/report-schedule-settings')
	declare mmReportScheduleSettingsService: Service.ModelManager.ReportScheduleSettings;

	@tracked report: Report | null;
	@tracked template: Template | null;

	@tracked currentVisibleSlide: ReportSlide | null = null;
	@tracked isLoaderShown = false;

	@tracked isDownloadingSnapshot = false;

	requestedReportId: number | null = null;
	requestedReportSlidesIds: number[] = [];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.args.model.on(PopupEvent.CLOSED, this, () => {
			this.mmBoardsService.checkMaxBoardIdsToRemember();
		});

		this.report = (this.popupData as PopupData.Report.Preview.ForReport).report || null;
		this.template = (this.popupData as PopupData.Report.Preview.ForTemplate).template || null;

		const template = this.template;
		const report = this.report;

		if (template) {
			this.isLoaderShown = true;

			this.requestService
				.api<RecordData.Payload>(
					'GET',
					`/token/${template.token}/report/${template.objectId}`,
				)
				.then(
					(reply) => {
						// recalculate popup size (when report is loaded and has info about notes for slides)
						this.scheduleUpdatePopupSize();

						// Don't push entire response here, especially not reply.template because the object is very different from what /d/templates?type=1&isPublic=true/false returns
						if (template.isPublic) {
							this.storeService.pushPayload(
								pick(reply, 'report', 'reportSlideFragments', 'reportSlides'),
							);
						} else {
							this.storeService.pushPayload(
								pick(reply, 'reportSlideFragments', 'reportSlides'),
							);
						}

						const reportId = reply.report!.id, // the report token reply carries its report
							reportModel = this.mmReportsService.findModel(String(reportId));

						this.report = reportModel;
						this.requestedReportId = reportId;

						if (this.report?.isPageless) {
							reportModel?.slides.forEach((slide) => {
								this.getReportSlideData(slide);
							});
						} else {
							const firstReportSlide = reportModel?.sortedSlides?.[0];
							if (firstReportSlide) {
								this.getReportSlideData(firstReportSlide);
							}
						}
					},
					(errorData) => {
						this.close();
						throw errorData;
					},
				)
				.finally(() => {
					this.isLoaderShown = false;
				})
				.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
		} else if (report) {
			this.isLoaderShown = true;

			let promise;
			if (!this.mmReportsService.isReportDataRequested(report.id!)) {
				promise = this.mmReportsService.requestReportData(report.id!);
			} else {
				promise = Promise.resolve();
			}

			void promise.then(() => {
				this.isLoaderShown = false;
				this.currentVisibleSlide = report.sortedSlides[0] || null;

				// recalculate popup size (when report is loaded and has info about notes for slides)
				this.scheduleUpdatePopupSize();
			});

			if (report.id) {
				this.mmActivityLogsService.createAndSaveActivityLogItem(
					ActivityLogEvent.REPORT_VIEW,
					report.id,
				);
			}
		}

		// just approximate max size, will be re-adjusted when content loads (in updatePopupSize)
		const wallSize = getMaxSizeForRatio($(window), 1.638);

		this.updatePopupStyle({
			width: wallSize.width,
			height: wallSize.height,
			top: (window.innerHeight - wallSize.height) / 2,
			left: (window.innerWidth - wallSize.width) / 2,
		});

		schedule('afterRender', () => {
			$(window).on('resize.modalBoard', () => {
				run(this, this.scheduleUpdatePopupSize);
			});

			this.scheduleUpdatePopupSize();

			later(() => {
				if (
					this.tourService.isEligibleForReportsPreviewPopupEditTour &&
					this.report?.canCurrentUserEdit
				) {
					this.tourService.initTour(TourId.REPORTS__PREVIEW_POPUP_EDIT)?.start();
				}

				if (
					!this.tourService.isEligibleForReportsPreviewPopupEditTour &&
					this.tourService.isEligibleForReportsPreviewPopupShareTour &&
					this.report?.canCurrentUserEdit
				) {
					this.tourService.initTour(TourId.REPORTS__PREVIEW_POPUP_SHARE)?.start();
				}
			}, 2000);
		});
	}

	get trackingOrigin():
		| TrackingOriginTypeForPreview
		| TrackingOriginTypeForReportReminderPopupOpen
		| TrackingOriginForTemplateCreated
		| null {
		return (this.popupData as PopupData.Report.Preview.ForReport).trackingOrigin || null;
	}

	getReportSlideData(reportSlide: ReportSlide): void {
		const reportSlideId = reportSlide.numericId!;

		if (this.requestedReportSlidesIds.includes(reportSlideId)) {
			return;
		}

		const template = this.template;
		if (!template) {
			return;
		}

		if (
			!reportSlide.fragments.some(
				(fragment) =>
					fragment.isTypeBlock || fragment.isTypeBoard || fragment.isTypeStoryText,
			)
		) {
			return;
		}

		this.isLoaderShown = true;

		// requesting on the "public" URL since this board might not be in the current space
		this.mmReportSlidesService
			.getReportSlideDataWithToken(template.token, reportSlide.id!)
			.then(() => {
				this.currentVisibleSlide = reportSlide;
				this.requestedReportSlidesIds.push(reportSlideId);
			})
			.catch(() => {
				this.close();
			})
			.finally(() => {
				this.isLoaderShown = false;
			});
	}

	// ------ helpers for popup resizes ------

	scheduleUpdatePopupSize(): void {
		scheduleOnce('afterRender', this, this.updatePopupSize);
	}

	updatePopupSize(): void {
		if (!this.report) {
			return; // prevent calculating popup size if there is no report loaded
		}

		// if report is pageless, there is no description content displayed
		const anySlideWithDescription =
				!this.report?.isPageless &&
				this.report?.slides.some((slide) => !!slide.descriptionContent),
			currentSlideWithDescription =
				!this.report?.isPageless && !!this.currentVisibleSlide?.descriptionContent;

		const navW = 165, // css .report_ct .report_nav
			topH = 64, // css .popup-type-board-preview.wall div.title
			paddingLeftRightBottom = 5,
			descriptionHeight = 135; // same in css .popup-type-board-preview #report-viewer_body .report_slide_desc

		const helpersWidth = navW + 2 * paddingLeftRightBottom,
			maxWidth = window.innerWidth * 0.9 - helpersWidth;

		const maxHelpersHeight =
				topH + paddingLeftRightBottom + (anySlideWithDescription ? descriptionHeight : 0),
			actualHelpersHeight =
				topH +
				paddingLeftRightBottom +
				(currentSlideWithDescription ? descriptionHeight : 0),
			maxHeight = window.innerHeight * 0.9 - maxHelpersHeight;

		const ratio = 1.78; // 16:9

		let slideCtWidth = maxWidth,
			slideCtHeight = slideCtWidth / ratio;

		if (slideCtHeight > maxHeight) {
			slideCtHeight = maxHeight;
			slideCtWidth = ratio * slideCtHeight;
		}

		// height adjusts to current slide's description, but top position to max height (if any other slide has description)
		this.updatePopupStyle({
			width: slideCtWidth + helpersWidth,
			height: slideCtHeight + actualHelpersHeight,
			top: (window.innerHeight - slideCtHeight - maxHelpersHeight) / 2,
			left: (window.innerWidth - slideCtWidth - helpersWidth) / 2,
		});

		this.mmReportsService.triggerInnerSlideResize();
	}

	updatePopupStyle(props: Record<string, number>): void {
		const popupStyle = htmlSafe(
			Object.keys(props)
				.map((key) => {
					return key + ':' + String(props[key]) + 'px';
				})
				.join(';'),
		);

		const popupCM = this.args.model;
		if (popupCM) {
			schedule('afterRender', () => {
				popupCM.popupStyle = popupStyle;
			});
		}
	}

	// ---- Actions -----

	get options(): Option[] {
		const options: Option[] = [],
			report = this.report!;

		options.push({
			icon: 'Download',
			text: 'Download snapshot',
			action: 'downloadSnapshot',
			isLoading: this.isDownloadingSnapshot,
		});

		if (report.canCurrentUserEdit) {
			options.push({
				icon: 'Share',
				text: 'Share',
				action: 'openSharePopup',
				class: 'share-link', // for tour
			});
		}

		options.push({
			icon: 'Play',
			text: 'Present',
			action: 'present',
		});

		if (report.canCurrentUserEdit) {
			if (!report?.isPageless) {
				options.push({
					icon: 'PresentNotes',
					text: 'Present with notes',
					action: 'presentWithNotes',
				});
			}

			if (report && !report.isTemplate) {
				options.push({
					icon: 'FolderUpload',
					text: 'Save as template',
					action: 'openAccountTemplateEditPopup',
					extra: this.billingService.accountTemplatesEnabled
						? undefined
						: {
								type: 'featureLimitation',
							},
				});
			}

			const isFavorite = !!this.report?.favorite;
			options.push({
				class: isFavorite ? 'accent' : '',
				text: isFavorite ? 'Remove from favorites' : 'Favorite',
				action: 'toggleFavorite',
				icon: isFavorite ? 'StarFill' : 'Star',
			});

			options.push({
				icon: 'Verified',
				text: report.isVerified ? 'Unverify' : 'Verify',
				action: 'verify',
				isDisabled: !report.verificationInfo,
			});

			options.push({
				icon: 'Copy',
				text: 'Duplicate',
				action: 'duplicate',
			});

			options.push({
				icon: 'Spaces',
				text: 'Add to Space',
				action: 'addToTeam',
			});

			if (this.currentUserService.isAdmin) {
				options.push(this.mmActivityLogsService.getObjectForOptionsPopup());
			}

			options.push({
				class: 'delete',
				text: 'Delete',
				action: 'deleteReport',
				icon: 'Trash',
			});
		}

		return options;
	}

	@action
	doAction(actionName?: string): void {
		if (!actionName) {
			return;
		}

		if (this.checkToOpenPricingLimitations()) {
			return;
		}

		this[actionName as Action]();
	}

	@action
	verify(): void {
		if (this.report) {
			void this.mmReportsService.toggleVerification(this.report);
		}
	}

	checkToOpenPricingLimitations() {
		return (
			!this.report?.isTemplate &&
			!this.billingService.checkIfCan(BillingFeature.REPORTS_BASIC, true)
		);
	}

	@action
	present() {
		const report = this.report;
		if (report) {
			this.mmReportsService.startPresent(report, 'reports_preview');
		}
	}

	@action
	presentWithNotes() {
		const report = this.report;
		if (report) {
			this.mmReportsService.startPresentWithNotes(report, 'reports_preview');
		}
	}

	@action
	useTemplate(): boolean {
		if (this.currentUserService.handleDemo()) {
			return false;
		}

		const onTemplateUse = (this.popupData as PopupData.Report.Preview.ForTemplate)
			.onTemplateUse;
		if (this.template && onTemplateUse) {
			onTemplateUse(this.template);
			return true;
		}

		return false;
	}

	@action
	editReport(): void {
		if (this.checkToOpenPricingLimitations()) {
			return;
		}

		const report = this.report;
		if (report) {
			const selectedSlide = this.currentVisibleSlide;
			this.mmReportsService.editReport(report, selectedSlide || undefined);
		}
	}

	@action
	openSharePopup(): void {
		if (this.checkToOpenPricingLimitations()) {
			return;
		}

		const report = this.report;
		if (report) {
			this.mmReportsService.openSharePopup(
				report,
				this.trackingOrigin as TrackingOriginTypeForShare,
			);
		}
	}

	@action
	openAccountTemplateEditPopup(): void {
		const report = this.report;
		if (report) {
			this.mmReportsService.openAccountTemplateEditPopup(
				report,
				this.trackingOrigin as TrackingOriginForTemplateCreated,
			);
		}
	}

	@action
	deleteReport(): void {
		const report = this.report;
		if (report) {
			void this.mmReportsService.deleteReport(report).then(() => {
				this.close();
			});
		}
	}

	@action
	onSlideChange(slideIndex: number): void {
		const report = this.report;
		if (report) {
			const slide = report.sortedSlides[slideIndex];
			if (slide) {
				this.currentVisibleSlide = slide;
				this.getReportSlideData(slide);
				this.scheduleUpdatePopupSize();
			}
		}
	}

	@action
	addToTeam() {
		const itemId = this.report?.id;

		if (itemId) {
			this.mmTeamSectionsService.openAddPopup({
				objectId: itemId,
				objectType: TeamSectionItemType.REPORT,
			});
		}
	}

	@action
	openActivityLog() {
		const report = this.report;
		if (!report) {
			return;
		}
		this.mmActivityLogsService.createAndOpenPopup({
			model: report,
		});
	}

	@action
	toggleFavorite(): void {
		const report = this.report;
		if (!report) {
			return;
		}
		this.mmReportsService.toggleReportFavorite(report);
	}

	@action
	downloadSnapshot() {
		if (this.isDownloadingSnapshot) {
			return;
		}

		if (!this.billingService.checkIfCan(BillingFeature.SHARING, true)) {
			return;
		}

		this.isDownloadingSnapshot = true;
		void this.mmReportsService.downloadSnapshotWithInfolayer(this.report!).then(() => {
			this.isDownloadingSnapshot = false;
		});
	}

	@action
	duplicate(): void {
		const report = this.report;
		if (!report) {
			return;
		}

		const duplicatePromise = this.mmReportsService.duplicateReport(report);
		if (!duplicatePromise) {
			return;
		}
		void duplicatePromise.then((createdReport) => {
			this.analyticsService.event(TrackingEvent.REPORT_CREATED, { origin: 'page' });
			this.mmReportsService.openPreviewPopup(createdReport);
		});
	}

	// ----- Cleanup -----

	override willDestroy() {
		const requestedReportId = this.requestedReportId;
		if (requestedReportId && this.template?.isPublic) {
			this.garbageCollectorService.clearConnectedRecords('report', {
				id: String(requestedReportId),
			});
			this.report?.model.unloadRecord();
		}

		this.mmBoardPreviewsService.clearAllRequestedPreviews();
		this.popupService.closeOnlyContextPopups();
	}
	<template>
		<div class="close-bg"></div>

		<div class="title bgTheme-dark">

			{{#if this.template}}

				<CommonRenderingPreviewHeaderComponent
					@model={{this.template}}
					@isEditBtnShown={{this.report.canCurrentUserEdit}}
					@isShownInPopup={{true}}
					@onUse={{this.useTemplate}}
					@onEdit={{this.editReport}}
				/>

			{{else if this.report}}

				<div class="boardHeader">

					<h1>{{this.report.title}}</h1>
					<VerificationBadgeComponent
						@verification={{this.report.verificationInfo}}
						@entityLabel="report"
						@size="md"
					/>

					<div class="actions-right">
						<div class="dbx-ui-actions">

							<CommonCollapsiblePillOptionsComponent
								@itemsList={{this.options}}
								@doAction={{this.doAction}}
							/>

							<NotificationsButtonComponent
								@model={{this.report}}
								@origin="report_preview"
								@popupTheme="dark"
								@buttonTheme="empty"
							/>

							{{#if this.report.canCurrentUserEdit}}
								<a class="button edit" {{on "click" this.editReport}}>
									<CommonUiIconComponent @name="Pencil" @size="sm" />Edit
								</a>
							{{/if}}

						</div>
					</div>

				</div>

			{{/if}}

		</div>

		<div class="content">

			<div id="datawall" class="border">
				<ReportsViewerComponent
					@model={{this.report}}
					@selectedSlide={{this.currentVisibleSlide}}
					@onSlideChange={{this.onSlideChange}}
					@isLoaderShown={{this.isLoaderShown}}
				/>

			</div>

		</div>
	</template>
}
